import { cn } from '@/lib/utils';
import type { DappGeneratedFile } from '@/types/dapp-builder';
import { Check, ChevronDown, ChevronRight, Copy, FileCode2 } from 'lucide-react';
import { useState } from 'react';

/* ================================================================== */
/*  File Tree                                                         */
/* ================================================================== */

interface TreeNode {
  name: string;
  path: string;
  children: TreeNode[];
  file?: DappGeneratedFile;
}

function buildTree(files: DappGeneratedFile[]): TreeNode[] {
  const root: TreeNode[] = [];

  for (const file of files) {
    const parts = file.path.split('/');
    let current = root;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const existing = current.find((n) => n.name === part);

      if (i === parts.length - 1) {
        // File node
        current.push({ name: part, path: file.path, children: [], file });
      } else if (existing) {
        current = existing.children;
      } else {
        const dir: TreeNode = { name: part, path: parts.slice(0, i + 1).join('/'), children: [] };
        current.push(dir);
        current = dir.children;
      }
    }
  }

  return root;
}

function TreeItem({
  node,
  selectedPath,
  onSelect,
  depth = 0,
}: {
  node: TreeNode;
  selectedPath: string | null;
  onSelect: (path: string) => void;
  depth?: number;
}) {
  const [expanded, setExpanded] = useState(depth < 2);
  const isDir = node.children.length > 0 && !node.file;
  const isSelected = selectedPath === node.path;

  return (
    <div>
      <button
        onClick={() => {
          if (isDir) setExpanded(!expanded);
          else onSelect(node.path);
        }}
        className={cn(
          'flex items-center gap-1 w-full text-left px-2 py-0.5 rounded text-[10px] transition-colors',
          isSelected ? 'bg-indigo-500/20 text-indigo-200' : 'text-[#ccc] hover:bg-[#333]',
        )}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
      >
        {isDir ? (
          expanded ? (
            <ChevronDown className="w-3 h-3 text-[#888] shrink-0" />
          ) : (
            <ChevronRight className="w-3 h-3 text-[#888] shrink-0" />
          )
        ) : (
          <FileCode2 className="w-3 h-3 text-[#888] shrink-0" />
        )}
        <span className="truncate">{node.name}</span>
      </button>

      {isDir && expanded && (
        <div>
          {node.children.map((child) => (
            <TreeItem
              key={child.path}
              node={child}
              selectedPath={selectedPath}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/*  File Preview Component                                            */
/* ================================================================== */

interface DappFilePreviewProps {
  files: DappGeneratedFile[];
}

export default function DappFilePreview({ files }: DappFilePreviewProps) {
  const [selectedPath, setSelectedPath] = useState<string | null>(files[0]?.path ?? null);
  const [copied, setCopied] = useState(false);

  const tree = buildTree(files);
  const selectedFile = files.find((f) => f.path === selectedPath);

  const handleCopy = () => {
    if (selectedFile) {
      navigator.clipboard.writeText(selectedFile.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex h-[400px] -mx-5 -mb-5 border-t border-[#333]">
      {/* File tree */}
      <div className="w-52 shrink-0 border-r border-[#333] overflow-y-auto py-2">
        <p className="text-[9px] font-medium text-[#555] uppercase px-3 mb-2">
          {files.length} files generated
        </p>
        {tree.map((node) => (
          <TreeItem
            key={node.path}
            node={node}
            selectedPath={selectedPath}
            onSelect={setSelectedPath}
          />
        ))}
      </div>

      {/* Code view */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {selectedFile ? (
          <>
            <div className="flex items-center justify-between px-3 py-1.5 bg-[#252526] border-b border-[#333]">
              <span className="text-[10px] text-[#aaa] truncate">{selectedFile.path}</span>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 text-[9px] text-[#888] hover:text-white transition-colors"
              >
                {copied ? (
                  <Check className="w-3 h-3 text-green-400" />
                ) : (
                  <Copy className="w-3 h-3" />
                )}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <pre className="flex-1 overflow-auto p-3 text-[10px] font-mono text-[#d4d4d4] bg-[#1a1a1a] leading-relaxed">
              {selectedFile.content}
            </pre>
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-[#555] text-xs">
            Select a file to preview
          </div>
        )}
      </div>
    </div>
  );
}
